import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { MatchingScene } from '@/types/gameSpec';
import { shuffle, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

/**
 * Click a left item, then click a right item to pair them.
 * Auto-completes when all left items are paired.
 */
export const MatchingSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<MatchingScene>) => {
  const lefts = scene.pairs.map((p) => p.left);
  const rightsOrder = useMemo(
    () => shuffle(scene.pairs.map((_, i) => i)),
    [scene.id],
  );

  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  // pairings[leftIndex] = rightIndex (in original pairs array)
  const [pairings, setPairings] = useState<Record<number, number>>({});
  const [revealed, setRevealed] = useState(false);

  const usedLefts = new Set(Object.keys(pairings).map(Number));
  const usedRights = new Set(Object.values(pairings));

  const tryFinish = (next: Record<number, number>) => {
    if (Object.keys(next).length === scene.pairs.length) {
      setRevealed(true);
      const ok = Object.entries(next).every(
        ([l, r]) => Number(l) === Number(r),
      );
      setTimeout(() => onAnswer(ok, { pairings: next }), 900);
    }
  };

  const pickLeft = (i: number) => {
    if (revealed || usedLefts.has(i)) return;
    setSelectedLeft(i);
  };

  const pickRight = (rIdx: number) => {
    if (revealed || selectedLeft === null || usedRights.has(rIdx)) return;
    const next = { ...pairings, [selectedLeft]: rIdx };
    setPairings(next);
    setSelectedLeft(null);
    tryFinish(next);
  };

  const reset = () => {
    if (revealed) return;
    setPairings({});
    setSelectedLeft(null);
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.matching}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            {lefts.map((label, i) => {
              const paired = usedLefts.has(i);
              const correct = revealed && pairings[i] === i;
              const wrong = revealed && paired && pairings[i] !== i;
              return (
                <Button
                  key={i}
                  variant={selectedLeft === i ? 'default' : 'outline'}
                  className={cn(
                    'w-full justify-start whitespace-normal h-auto py-2',
                    paired && !revealed && 'opacity-60',
                    correct && 'border-green-500 bg-green-50 text-green-900',
                    wrong && 'border-destructive bg-red-50 text-red-900',
                  )}
                  onClick={() => pickLeft(i)}
                  disabled={paired || revealed}
                >
                  {label}
                </Button>
              );
            })}
          </div>
          <div className="space-y-2">
            {rightsOrder.map((rIdx) => {
              const label = scene.pairs[rIdx].right;
              const paired = usedRights.has(rIdx);
              const pairedLeft = Object.entries(pairings).find(
                ([, v]) => v === rIdx,
              )?.[0];
              const correct = revealed && pairedLeft && Number(pairedLeft) === rIdx;
              const wrong = revealed && paired && !correct;
              return (
                <Button
                  key={rIdx}
                  variant="outline"
                  className={cn(
                    'w-full justify-start whitespace-normal h-auto py-2',
                    paired && !revealed && 'opacity-60',
                    correct && 'border-green-500 bg-green-50 text-green-900',
                    wrong && 'border-destructive bg-red-50 text-red-900',
                  )}
                  onClick={() => pickRight(rIdx)}
                  disabled={paired || revealed || selectedLeft === null}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </div>

        {!revealed && Object.keys(pairings).length > 0 && (
          <Button variant="ghost" size="sm" onClick={reset}>
            Reset pairs
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
